import React, { useState, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { getCaptchaToken, verifyCaptcha } from '../utils/captcha';
import { submitForm } from '../utils/api';
import { toast } from 'sonner';

export default function DealInterestPopup({ isOpen, onClose, deal }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // reCAPTCHA v3 verification
      const token = await getCaptchaToken('deal_interest');
      const captchaResult = await verifyCaptcha(token, 'deal_interest');
      if (!captchaResult.success) {
        toast.error('Verification failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      await submitForm('deal_interest', {
        dealId: deal?.id,
        dealTitle: deal?.title,
        sector: deal?.sector,
        ...formData
      });
      setSuccess(true);
      setIsSubmitting(false);
      
      setTimeout(() => {
        setSuccess(false);
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        onClose();
      }, 3000);
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Failed to submit interest. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-300">
        <button 
          onClick={onClose}
          className="absolute top-5 right-5 p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-[111]"
        >
          <X className="h-6 w-6" />
        </button>
        
        <div className="p-8">
          {success ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-white mb-3">Interest Logged!</h3>
              <p className="text-slate-400 text-lg">Thank you for your interest in {deal?.id}. Our team will contact you shortly.</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-white mb-2 italic">I'm Interested in this deal</h2>
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 mt-4">
                  <div className="text-amber-500 font-bold text-sm uppercase tracking-widest mb-1">{deal?.id}</div>
                  <div className="text-xl font-bold text-white">{deal?.title}</div>
                  <div className="text-slate-400 text-sm">{deal?.sector} • {deal?.stage}</div>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300 ml-1">Full Name *</label>
                    <Input required placeholder="Your Name" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="bg-slate-800 border-slate-700 text-white h-12" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300 ml-1">Email Address *</label>
                    <Input required type="email" placeholder="email@example.com" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} className="bg-slate-800 border-slate-700 text-white h-12" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300 ml-1">Phone Number</label>
                    <Input placeholder="+91 ..." value={formData.phone} onChange={e=>setFormData({...formData, phone: e.target.value})} className="bg-slate-800 border-slate-700 text-white h-12" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300 ml-1">Company / Organization</label>
                    <Input placeholder="Your Company Name" value={formData.company} onChange={e=>setFormData({...formData, company: e.target.value})} className="bg-slate-800 border-slate-700 text-white h-12" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300 ml-1">Additional Information (Optional)</label>
                  <Textarea placeholder="Share any specific queries or your investment focus..." value={formData.message} onChange={e=>setFormData({...formData, message: e.target.value})} className="bg-slate-800 border-slate-700 text-white min-h-[120px] resize-none" />
                </div>
                
                <Button type="submit" disabled={isSubmitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white py-7 text-xl font-bold rounded-xl shadow-lg shadow-amber-600/20 transition-all active:scale-95">
                  {isSubmitting ? (
                    <span className="flex items-center"><X className="animate-spin mr-2 h-5 w-5" /> Submitting...</span>
                  ) : (
                    <span className="flex items-center">Send Interest Alert <Send className="ml-2 h-5 w-5" /></span>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
